import { IReview, IReviewItem/*, IReview*/ } from "./interfaces";
import { IImage } from "~/ui/review/photosupload";
import { IReviewConf } from "~/interfaces";
import { store } from "~/state/store";
import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import { getFirmLayout, parseStructuredRating } from "~/util/review";
import { IAlternativeItem } from "@mapy/review-components/dist/types/interfaces";

export interface IReviewPopupOptions {
	data: IReviewConf;
	logSource?: string;
	logPosition?: string;
	textReview?: boolean;
	showThankYou?: boolean;
	onClose?: (data: { review: IReview; myReview: IReviewItem; wasRemoved: boolean }) => void;
	onBack?: (myReview: IReviewItem, images?: Array<IImage>) => void;
	logObj?: object;
	activeRequest?: boolean;
	fromOtherPOI?: boolean;
	forceRating?: number;
	initialText?: string;
	initialImages?: Array<IImage>;
}

const DEFAULT_OPTIONS: IReviewPopupOptions = {
	data: null,
	logSource: "recenze-poi",
	logPosition: "bottom",
	textReview: false,
	showThankYou: true,
	logObj: {},
	activeRequest: false,
	fromOtherPOI: false,
	forceRating: 0,
	initialText: "",
	initialImages: [],
};

export default class ReviewPopup {
	#options: IReviewPopupOptions;

	#reviewElement: HTMLMapyReviewDialogElement;

	#myLastReview: IReviewItem;

	#lastReview: IReview;

	#isBack: boolean;

	#starsChanged: boolean;

	#logOrigin: string;

	#textInputLogged: boolean;

	#alternativesShown: boolean;

	#alternativesList: Array<IAlternativeItem>;

	constructor(options: IReviewPopupOptions) {
		this.#options = {
			...DEFAULT_OPTIONS,
			...options,
		};

		this.#myLastReview = this.#options.data?.myReview || null;
		this.#lastReview = this.#options.data?.review || null;
		// slo se nekdy zpet?
		this.#isBack = false;
		// pokud se slo zpet, zmenily se hvezdy?
		this.#starsChanged = false;
		this.#logOrigin = "step0";
		this.#textInputLogged = false;
		// videl uzivatel alternativy?
		this.#alternativesShown = false;
		this.#alternativesList = [];

		if (!this.#options.data) {
			// nejsou data, nic nebude - zalogovat chybu?
			return;
		}

		const state = store.getState();
		const { user } = state.user;
		const reviewElement = node("mapy-review-dialog");

		reviewElement.addEventListener("signal", event => {
			// uzivatel zadal hodnoceni
			if (event.detail.name === "put-review") {
				if (event.detail.error) {
					if (event.detail.page === "PAGE-STARS-STRUCTURED" && event.detail.data?.structured_rating?.length === 0) {
						this.#logOrigin = "step1Failed";
						this.#log("reviews_fail_stars");
					} else {
						this.#logOrigin = "step3Failed";
						this.#log("reviews_fail_text");
					}
				} else {
					const { review, ...rest } = event.detail.response;

					// zapamatujeme si posledni odpoved
					this.#lastReview = rest as IReview;
					this.#myLastReview = review as unknown as IReviewItem;

					if (this.#myLastReview?.structured_rating?.length && event.detail.page === "PAGE-STARS-STRUCTURED") {
						this.#log("attributes_set", parseStructuredRating(this.#myLastReview.structured_rating.map(structure => ({ key: structure.key, value: structure.rating })), true));
					} else if (event.detail.page === "PAGE-TEXT-PHOTO") {
						this.#log("recenze-leti", {
							type: this.#myLastReview.text ? "recenze-text" : "recenze-stars",
							photoCount: this.#myLastReview.gallery?.length || 0,
							...this.#myLastReview.structured_rating ? parseStructuredRating(this.#myLastReview.structured_rating.map(structure => ({ key: structure.key, value: structure.rating }))) : {},
						});
					}
				}
			// uzivatel recenzi smazal
			} else if (event.detail.name === "remove-review") {
				this.#myLastReview = null;
				this.#lastReview = this.#options.data?.review || null;
			} else if (event.detail.name === "back-to-page-stars-structured") {
				this.#isBack = true;
				this.#options.onBack?.(this.#myLastReview);
				if (this.#myLastReview?.structured_rating?.length === 0) {
					this.#logOrigin = "step1Edit";
				} else {
					this.#logOrigin = "attributesDialogue";
				}
			// slo se zpet a zase se jde vpred
			} else if (event.detail.name === "save-click") {
				// jsme na hvezdach pri zpet
				if (this.#isBack && this.#logOrigin === "step1Edit") {
					this.#log("reviews_edit_stars", {
						starsEdit: this.#starsChanged ? 1 : 0,
					});
				// jsme na textu/fotkach
				} else if (this.#logOrigin === "step2" || this.#logOrigin === "step3") {
					if (event.detail.data.text) {
						this.#log("click", {
							text: "Přidat komentář",
						});
					} else {
						this.#log("click", {
							text: "Již nechci nic dodat",
						});
					}
				}
			// kliklo se na hvezdy
			} else if (event.detail.name === "stars-click") {
				if (this.#isBack) {
					this.#starsChanged = true;
				}

				this.#log("click", {
					text: `stars-updated-${event.detail.rating * 2}`,
				});
			// pridal fotku
			} else if (event.detail.name === "photos-add") {
				this.#log("click", {
					text: "photoAdd",
				});
			} else if (event.detail.name === "stars-structured-show") {
				this.#logOrigin = "step0";

				if (event.detail.structuredItems.length) {
					this.#log("attributes_dialogue_show", {
						attributesCount: event.detail.structuredItems.length,
					});
				}
			} else if (event.detail.name === "text-form-show") {
				this.#logOrigin = "step2";
			// kliklo se na potvrzeni smazani recenze (tedy zavirame dialog a mazeme, co do tehle doby zadal)
			} else if (event.detail.name === "confirmation-close") {
				this.#logOrigin += "Delete";
				this.#log("click", {
					text: "Smazat",
				});
			// kliklo se na zruseni zavreni dialogu a jsme v nem zpet
			} else if (event.detail.name === "confirmation-cancel") {
				this.#log("click", {
					text: "Zrušit",
				});
			// doslo k opusteni textarea a je v ni nejaky text
			} else if (event.detail.name === "textarea-blur") {
				// zadani textu logujeme pouze jednou za zivot dialogu
				if (!this.#textInputLogged) {
					this.#log("text_fill");
					this.#textInputLogged = true;
				}
			// klikani na odkazy v paticce
			} else if (event.detail.name === "footer-click") {
				if (event.detail.data?.footerType === "terms") {
					this.#log("click", {
						url: event.detail.data.href,
						text: "podmínkami",
					});
				} else if (event.detail.data?.footerType === "rules") {
					this.#log("click", {
						url: event.detail.data.href,
						text: "pravidly",
					});
				}
			// zobrazily se alternativy
			} else if (event.detail.name === "show-alternatives") {
				this.#alternativesShown = true;
				this.#alternativesList = event.detail.items;
			// doslo k vyberu alternativy
			} else if (event.detail.name === "select-alternative" && this.#alternativesShown) {
				if (Mapy.getComponent("detail").isActive()) {
					const detailState = Mapy.getComponent("detail").getState();
					const alternative = this.#alternativesList[event.detail.index];

					if (detailState.source !== alternative.data.source || detailState.id !== alternative.data.id) {
						Mapy.getComponent("detail").setState({
							source: alternative.data.source,
							id: alternative.data.id,
						});
						Mapy.getComponent("history").save();
					}
				}
			} else if (event.detail.name === "after-review-remove") {
				this.#myLastReview = this.#options.data?.myReview || null;
				this.#lastReview = this.#options.data?.review || null;
			}
		});
		reviewElement.addEventListener("close", event => {
			reviewElement.open = false;
			reviewElement.remove();
			this.#log("reviews_form_close", {
				origin: this.#logOrigin,
			});
			if (!this.#myLastReview?.text && this.#myLastReview?.gallery?.length === 0 && event.detail.reason === "finished") {
				this.#log("recenze-leti", {
					type: "recenze-stars",
					photoCount: 0,
					...this.#myLastReview.structured_rating ? parseStructuredRating(this.#myLastReview.structured_rating.map(structure => ({ key: structure.key, value: structure.rating }))) : {},
				});
			}
			this.#options.onClose?.({
				myReview: event.detail.reason === "review-removed" ? null : this.#myLastReview,
				review: event.detail.reason === "review-removed"
					? this.#options.data?.review
						? {
							...this.#options.data.review,
							total: this.#options.data.myReview ? Math.max(this.#options.data.review.total - 1, 0) : this.#options.data.review.total,
						}
						: null
					: this.#lastReview,
				wasRemoved: event.detail.reason === "review-removed",
			});
		});
		reviewElement.lang = CONFIG.defaultLang;
		reviewElement.poiId = this.#options.data.id as number;
		reviewElement.poiSource = this.#options.data.source;
		reviewElement.user = user.name || user.username;
		reviewElement.verifiedUser = user.bankId;
		reviewElement.endpoint = `${location.origin}${CONFIG.requestUrl}/review-comp`;
		reviewElement.apiKey = CONFIG.mapiKey;
		reviewElement.anonymous = CONFIG.wasmEnabled;
		if (this.#options.forceRating) {
			reviewElement.forceInsert = true;
		}
		reviewElement.alternatives = !!this.#options.data.review?.alternatives?.length;
		reviewElement.detailData = {
			source: this.#options.data.source,
			id: this.#options.data.id,
			typeId: this.#options.data.typeId,
			typeName: this.#options.data.typeName,
			description: "",
			inactive: this.#options.data.inactive,
			mark: {
				lon: this.#options.data.mark?.lon || 0,
				lat: this.#options.data.mark?.lat || 0,
			},
			media: {},
			title: this.#options.data.title,
			review: {
				...this.#options.data.review,
				// eslint-disable-next-line camelcase
				structured_rating: this.#options.data.structuredRating.map(singleRating => ({
					...singleRating,
					rating: null,
				})),
			},
			extend: {
				poi: {
					alternatives: this.#options.data.review?.alternatives || [],
				},
			},
		};
		if (this.#options.forceRating) {
			reviewElement.reviewData = {
				rating: this.#options.forceRating,
				updated: "",
				id: 0,
				weight: 0,
				text: "",
				state: "new",
				gallery: [],
				lang: "",
				topped: false,
				type: "generic",
				// eslint-disable-next-line camelcase
				review_source: "",
				rejectReason: "",
				// eslint-disable-next-line camelcase
				update_request: false,
				// eslint-disable-next-line camelcase
				structured_rating: this.#options.data.structuredRating.map(singleRating => ({
					...singleRating,
					rating: null,
				})),
			};
		}
		if (this.#options.data.myReview) {
			reviewElement.reviewData = {
				...this.#options.data.myReview as any,
			};
		}
		reviewElement.switchStars = true;
		document.body.appendChild(reviewElement);
		reviewElement.open = true;

		this.#reviewElement = reviewElement;
	}

	close() {
		this.#reviewElement.open = false;
	}

	#log(name: string, data?: object) {
		/* eslint-disable camelcase */
		Mapy.getComponent("logger").log(name, {
			position: this.#options.logPosition,
			rating_layout: getFirmLayout(this.#options.data.review, this.#options.data.booking, null, this.#options.data.isFirm),
			sourcePoi: this.#options.data.source,
			typeid: this.#options.data.typeId,
			firmTwin: this.#options.data.isTwin ? 1 : 0,
			source: this.#options.logSource,
			section: "reviews",
			type: "recenze",
			...this.#options.logObj,
			...data,
		});
	}
}
